import React, { useMemo, useState } from "react";
import useEscapeLayer from "../useEscape";
import TimelineRow from "./TimelineRow";
import RailGroup from "./RailGroup";
import { TabsHead, ProfileBody } from "./ProfileTab";
import { timelineView } from "./timelineView.mjs";
import { plural } from "./format.mjs";

// The tabs' header is shared with the Profile tab, and defined there (see its
// header for why); it is re-exported here, where the shell's contract names it.
export { TabsHead };

/*
 * ONE TIMELINE FOR THE WHOLE COMPANY (frames 1a, 1b, 1c, 1d, 1g), under the
 * Timeline · Profile tabs.
 *
 * The rows are GET /companies/:id/timeline (PR #156), read by
 * record/timelineModel.mjs buildTimeline (who on every row, the first line, the
 * subject once per thread per day, calendar mail folded under its meeting,
 * bodyless HubSpot tasks folded away, day groups that never cross a day) and laid
 * out by record/timelineView.mjs (the same-minute order, one row per call, the
 * notes under a meeting, the words the frames add, what starts open).
 *
 * The header, as drawn: the filter chips (All · Conversations · Calls & meetings ·
 * Stage moves · Notes), "Everyone at Pluto · 4 people · 29 entries" with "Only
 * Shahar", and Collapse all at the far end while anything is open. The scope has
 * its own line under the chips at every width, as 1a and 1b draw it: 1d squeezes
 * it beside the chips, which only fits a short company name, and a long one
 * ("Northwind Data") wrapped the line and lost the entry count. A short record shows
 * neither (1c, 1g): two entries need no filter. At the foot, "Showing 200 of 340
 * · Load older" on the before cursor (ops.loadOlder) or "Start of the record · 29
 * entries, 7 of them from colleagues’ records". ONE Add note, in the tabs' header.
 *
 * On the phone the tab is one line in the column, "Timeline · 2 entries ›"
 * (frame 1h), opening as a bottom sheet with both tabs inside, like every other
 * group there (the brief: rail groups open as bottom sheets). The sheet keeps its
 * own tab, so switching to Profile inside it never unmounts the sheet.
 *
 * Props:
 *   record   the shell's bundle: timeline ({ rows, total, has_more, loadingOlder }),
 *            company, contact, act, zones, layout, ui.tab
 *   ops      ops.setTab, ops.loadOlder, ops.call (the note), ops.reload,
 *            ops.meetingOutcome (rows), and what ProfileBody uses on the phone
 */

// The note, from the one Add note in the header: a note on the company's
// timeline, filed on the person whose record this is.
function NoteForm({ record, ops, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await ops.call("/activities", {
        method: "POST",
        body: JSON.stringify({
          type: "note",
          contact_id: record.contact.id,
          company_id: (record.company && record.company.id) || null,
          body: text.trim(),
        }),
      });
      await ops.reload();
      onDone();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  return (
    <div className="rec-tl__compose rec-tl__compose--top">
      <textarea className="rec-textarea" autoFocus placeholder="Note to the timeline" value={text} onChange={(e) => setText(e.target.value)} />
      <div className="rec-tl__composeactions">
        {error && <span className="rec-err">{error}</span>}
        <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" onClick={onDone}>Cancel</button>
        <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={!text.trim() || busy} onClick={save}>{busy ? "Saving…" : "Save note"}</button>
      </div>
    </div>
  );
}

// A person opened from People shows in the same panel: their filters, open rows
// and drafts must not carry over from the last person.
const personKey = (record) => String((record.contact && record.contact.id) || "none");

const asMap = (keys) => Object.fromEntries((keys || []).map((k) => [k, true]));

/** The list under the tabs: header, days, foot. */
export function TimelineBody({ record, ops }) {
  const [filter, setFilter] = useState("all");
  const [only, setOnly] = useState(null);
  // null until the reader opens or closes anything: until then the rows the
  // frames draw open (the newest move a person decided) are open.
  const [touched, setTouched] = useState(null);
  const [error, setError] = useState(null);
  const t = record.timeline;
  const v = useMemo(
    () =>
      timelineView(t.rows, {
        company: record.company,
        contact: record.contact,
        act: record.act,
        zones: record.zones,
        filter,
        only,
        total: t.total,
        hasMore: t.has_more,
        now: record.now || new Date(),
      }),
    [t.rows, t.total, t.has_more, record.company, record.contact, record.act, record.zones, record.now, filter, only]
  );
  const open = touched || asMap(v.defaultOpen);
  const anyOpen = Object.values(open).some(Boolean);
  const toggle = (key) => setTouched({ ...open, [key]: !open[key] });
  // Rows he opened are an Esc layer, as the old timeline's "collapse all · esc"
  // was: the brief's order is menu, confirm, expanded rows, then the panel. Keyed
  // on `touched`, so the row the frames draw open by default does not eat the
  // first Esc of a visit; only what he opened himself does.
  useEscapeLayer(!!touched && anyOpen, () => setTouched({}));

  const older = async () => {
    setError(null);
    try {
      await ops.loadOlder();
    } catch (e) {
      setError(e.message);
    }
  };

  const chips = v.showFilters && (
    <>
      {v.filters.map((f) => (
        <button
          key={f.id}
          type="button"
          className={`rec-chip${filter === f.id ? " is-on" : ""}`}
          aria-pressed={filter === f.id}
          disabled={f.id !== "all" && !f.count && filter !== f.id}
          onClick={() => setFilter(f.id)}
        >
          {f.label}
        </button>
      ))}
    </>
  );
  const s = v.scope;
  const scope = s && (
    <>
      {only && s.only ? (
        <>
          <span><b>{s.only.label}</b> · {s.only.count}</span>
          <button type="button" className="rec-link" onClick={() => setOnly(null)}>{s.label}</button>
        </>
      ) : (
        <>
          <span><b>{s.label}</b> · {s.rest}</span>
          {s.offer && <button type="button" className="rec-link" onClick={() => setOnly(s.offer.id)}>{s.offer.label}</button>}
        </>
      )}
    </>
  );
  const collapse = anyOpen && (
    <button type="button" className="rec-link rec-link--quiet" onClick={() => setTouched({})}>Collapse all</button>
  );

  return (
    <div className="rec-tl">
      {chips && <div className="rec-tl__filters">{chips}</div>}
      {(scope || collapse) && (
        <div className="rec-tl__scope">
          {scope}
          <span className="rec-tl__spacer" />
          {collapse}
        </div>
      )}

      {v.days.length > 0 && (
        <div className="rec-tl__list">
          {v.days.map((d) => (
            <React.Fragment key={d.key}>
              <div className="rec-tl__day">{d.heading}</div>
              {d.rows.map((row) => (
                <TimelineRow key={row.key} row={row} open={!!open[row.key]} onToggle={() => toggle(row.key)} ops={ops} />
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
      {v.empty && <div className="rec-tl__end">{v.empty}</div>}

      {(v.folded.calendarOrphans > 0 || v.folded.tasks > 0) && (
        // Calendar mail that found its meeting is counted on the meeting's row;
        // only what found none, and the empty HubSpot tasks, are counted here.
        <div className="rec-tl__end">
          {[
            v.folded.calendarOrphans ? `${plural(v.folded.calendarOrphans, "calendar email")} folded` : null,
            v.folded.tasks ? `${plural(v.folded.tasks, "empty HubSpot task")} folded away` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      )}
      {t.has_more && (
        <div className="rec-tl__older">
          {v.showing && <span className="rec-meta">{v.showing} · </span>}
          <button type="button" className="rec-link" disabled={t.loadingOlder} onClick={older}>{t.loadingOlder ? "Loading older…" : "Load older"}</button>
          {error && <span className="rec-err"> {error}</span>}
        </div>
      )}
      {v.start && <div className="rec-tl__end">{v.start}</div>}
    </div>
  );
}

// The phone's line and its sheet: both tabs inside, the tab kept by the sheet.
function PhoneTimeline({ record, ops }) {
  const [tab, setTab] = useState("timeline");
  const [noting, setNoting] = useState(false);
  const count = useMemo(
    () => timelineView(record.timeline.rows, { company: record.company, contact: record.contact, act: record.act, zones: record.zones, total: record.timeline.total, hasMore: record.timeline.has_more }).entries,
    [record.timeline.rows, record.timeline.total, record.timeline.has_more, record.company, record.contact, record.act, record.zones]
  );
  const total = record.timeline.has_more && record.timeline.total ? record.timeline.total : count;
  return (
    <RailGroup title="Timeline" summary={plural(total, "entry", "entries")} sheet id="timeline">
      <TabsHead
        record={record}
        ops={ops}
        tab={tab}
        onTab={setTab}
        right={tab === "timeline" && !noting ? <button type="button" className="rec-btn rec-btn--sm" onClick={() => setNoting(true)}>Add note</button> : null}
      />
      {tab === "timeline" && noting && <NoteForm record={record} ops={ops} onDone={() => setNoting(false)} />}
      {tab === "timeline" ? <TimelineBody key={personKey(record)} record={record} ops={ops} /> : <ProfileBody key={personKey(record)} record={record} ops={ops} />}
    </RailGroup>
  );
}

export default function RecordTimeline({ record, ops }) {
  const [noting, setNoting] = useState(false);
  if (record.layout === "phone") return <PhoneTimeline record={record} ops={ops} />;
  return (
    <section className="rec-tltab" aria-label="Timeline">
      <TabsHead
        record={record}
        ops={ops}
        right={!noting ? <button type="button" className="rec-btn rec-btn--sm" onClick={() => setNoting(true)}>Add note</button> : null}
      />
      {noting && <NoteForm record={record} ops={ops} onDone={() => setNoting(false)} />}
      <TimelineBody key={personKey(record)} record={record} ops={ops} />
    </section>
  );
}
